import { useState } from "react";
import Image from "components/base/Image";
import ImageVideoText from "components/sections/ImageVideoText";
import HorizontalTechSpecs from "components/sections/HorizontalTechSpecs";
import TextImageColumns from "components/sections/TextImageColumns";
import TextColumns from "components/sections/TextColumns";
import BigTitleSection from "components/sections/BigTitleSection";
import DarkQuoteSection from "components/sections/DarkQuoteSection";
import TextHeader from "components/sections/TextHeader";
import NavFooter from "components/sections/NavFooter";
import SpecsTable from "components/sections/SpecsTable";
import DiagramSection from "components/sections/DiagramSection";
import HorizontalSlideSection from "components/sections/HorizontalSlideSection";
import DataGridSection from "components/sections/DataGridSection";
import MainContainer from "components/sections/MainContainer";
import FloatingCTA from "components/blocks/FloatingCTA";
import MetaHead from "components/base/MetaHead";
import Trans from "components/base/Trans";
import HeroStreamVideoSection from "components/sections/HeroStreamVideoSection";
import { useShowCTA } from "utils/hooks";

export default function Engine() {
  const [lastSectionVisible, setLastSectionVisible] = useState(false);
  const { headerRef, showCTA } = useShowCTA(lastSectionVisible);

  return (
    <MainContainer
      navStyle={{ gradient: false, inverted: false }}
      updateVisible={setLastSectionVisible}
    >
      <MetaHead
        title="LBL_ENGINE_META_TITLE"
        description="LBL_ENGINE_META_DESC"
        imageUrl="/images/meta/engine.jpg"
      />
      <div ref={headerRef}>
        <TextHeader
          page={<Trans id="LBL_ENGINE_PAGE_TITLE" />}
          title={<Trans id="LBL_ENGINE_TITLE" />}
          description={<Trans id="LBL_ENGINE_DESCRIPTION" />}
          inverted
        />
      </div>
      <HeroStreamVideoSection
        height={["40vh", null, "100vh"]}
        minHeight={["100%", null, "700px"]}
        videoSrc="https://vz-49830c06-001.b-cdn.net/1ef70d20-4004-4181-a602-e39e3a38c8d1/playlist.m3u8"
        mobileVideoSrc="https://vz-49830c06-001.b-cdn.net/1ef70d20-4004-4181-a602-e39e3a38c8d1/playlist.m3u8"
        metadata={{
          video_title: "LBL_ENGINE_META_TITLE",
          video_id: "engine-header",
        }}
        mobileMetadata={{
          video_title: "LBL_ENGINE_META_TITLE",
          video_id: "engine-mobile-header",
        }}
        bgOverlay="transparent"
      />
      <DataGridSection inverted />
      <BigTitleSection
        inverted
        title={<Trans id="LBL_ENGINE_INNOVATION_TITLE" />}
        isWide
        description={<Trans id="LBL_ENGINE_INNOVATION_DESCRIPTION" />}
      />
      <ImageVideoText
        title={<Trans id="LBL_ENGINE_INNOVATION_CAPTION_1" />}
        description={<Trans id="LBL_ENGINE_INNOVATION_DESCRIPTION_1" />}
        image="/images/engine/img-engine-01.jpg"
        pb={0}
      />

      <TextImageColumns
        topTitle={<Trans id="LBL_ENGINE_EFFICIENCY_TITLE" />}
        topDescription={<Trans id="LBL_ENGINE_EFFICIENCY_DESCRIPTION" />}
        topImage="/images/engine/img-engine-03.jpg"
        bottomTitle={<Trans id="LBL_ENGINE_FADEC_TITLE" />}
        bottomDescription={<Trans id="LBL_ENGINE_FADEC_DESCRIPTION" />}
        bottomImage="/images/engine/img-engine-fadec.jpg"
        isInverted
      />

      <ImageVideoText
        title={<Trans id="LBL_ENGINE_INNOVATION_CAPTION_2" />}
        description={<Trans id="LBL_ENGINE_INNOVATION_DESCRIPTION_2" />}
        image="/images/engine/img-engine-02.jpg"
      />
      <TextColumns
        title={<Trans id="LBL_ENGINE_TURBINE_TITLE" />}
        description={<Trans id="LBL_ENGINE_TURBINE_DESCRIPTION" />}
        inverted
      />
      <HorizontalSlideSection
        bg="black.900"
        baseSrc="/images/engine/gallery/"
        items={[
          "01",
          "02",
          "03",
          "04",
          "05",
          "06",
          "07",
          "08",
          "09",
          "10",
          "11",
          "12",
          "13",
          "14",
          "15",
          "16",
          "17",
          "18",
          "19",
          "20",
          "21",
          "22",
          "23",
        ]}
      />

      <DiagramSection
        title={<Trans id="LBL_ENGINE_GT50_DIAGRAM_TITLE" />}
        subtitle={<Trans id="LBL_ENGINE_GT50_DIAGRAM_SUBTITLE" />}
        image="/images/engine/img-engine-shaft.jpg"
        step1={<Trans id="LBL_ENGINE_GT50_DIAGRAM_TEXT_1" />}
        step2={<Trans id="LBL_ENGINE_GT50_DIAGRAM_TEXT_2" />}
        step3={<Trans id="LBL_ENGINE_GT50_DIAGRAM_TEXT_3" />}
      />

      <HorizontalTechSpecs
        image="/images/navbar/navbar_gt50.jpg"
        video={{
          stream: {
            type: "hls",
            data: "https://vz-49830c06-001.b-cdn.net/c07a2406-c426-4f31-af3b-3391b79f04ae/playlist.m3u8",
          },
          metadata: {
            video_title: "GT50 Side View",
            video_id: "gt50-engine-specs",
            viewer_user_id: null,
          },
        }}
        specs={[
          { text: <Trans id="LBL_ENGINE_SPEC_1" /> },
          { text: <Trans id="LBL_ENGINE_SPEC_2" /> },
          { text: <Trans id="LBL_ENGINE_SPEC_3" /> },
          { text: <Trans id="LBL_ENGINE_SPEC_4" /> },
        ]}
      />
      <SpecsTable />
      <DarkQuoteSection
        childrenOnTop
        bg="black.700"
        title={<Trans id="LBL_ENGINE_GT50_TITLE" />}
        description={<Trans id="LBL_ENGINE_GT50_DESCRIPTION" />}
      >
        <Image
          src="/images/logo-gt50.png"
          alt="GT50 logo"
          width="200px"
          height="35px"
          objectFit="contain"
        />
      </DarkQuoteSection>
      <NavFooter
        previous={<Trans id="LBL_MENU_INTERIOR" />}
        next={<Trans id="LBL_MENU_GA2" />}
        pathPrevious="/interior-concept"
        pathNext="/general-aviation-20"
        bgPrevious="/images/nav-footer/interior.jpg"
        bgNext="/images/nav-footer/ga.jpg"
      />
      <FloatingCTA show={showCTA} />
    </MainContainer>
  );
}
